import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ClipboardList } from "lucide-react";
import { useSelector } from "react-redux";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  clearOpsDashboardOverviewCache,
  fetchOpsDashboardOverview,
} from "../../services/dashboardApi";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";

function Count({ label, value }) {
  return (
    <div className="rounded-lg bg-primary/[0.03] px-2.5 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/40">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-primary">{value}</p>
    </div>
  );
}

export default function NavTasks() {
  const token = useSelector((state) => state.auth.token);
  const [work, setWork] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const tick = useLiveEventTick(/^(portfolio\.|case\.|engagement\.|payment\.)/);

  useEffect(() => {
    if (!token || USE_MOCK_AUTH) return undefined;
    let cancelled = false;
    if (tick) clearOpsDashboardOverviewCache();
    fetchOpsDashboardOverview(token)
      .then((data) => {
        if (!cancelled) setWork(data?.work || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, tick]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  if (!token || USE_MOCK_AUTH) return null;

  const followPending = Number(work?.follow_up_pending ?? work?.follow_up_count ?? 0);
  const followTotal = Number(work?.follow_up_total ?? followPending);
  const assignments = work?.assignments || {};
  const assignPending = Number(assignments.pending || 0);
  const assignHref = assignments.href || "/dashboard/cases";
  const isAgent = work?.audience === "agent";
  const teamFollowUps = work?.team_follow_ups === true;
  const followUps = Array.isArray(work?.follow_ups) ? work.follow_ups : [];
  const badge = followPending + assignPending;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1.5 rounded-lg border border-primary/10 px-2 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/[0.03]"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Today's tasks"
      >
        <ClipboardList className="h-3.5 w-3.5 text-primary/50" />
        <span className="tabular-nums">{followPending}</span>
        <span className="hidden text-primary/45 lg:inline">{teamFollowUps ? "team follow-ups" : "follow-ups"}</span>
        <span className="text-primary/20">·</span>
        <span className="tabular-nums">{assignPending}</span>
        <span className="hidden text-primary/45 lg:inline">{isAgent ? "assigned" : "to assign"}</span>
        {badge > 0 ? (
          <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-primary">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-primary/10 bg-white p-3 shadow-card"
          role="dialog"
          aria-label="Today's tasks"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary/40">Today</p>

          <div className="mt-2 rounded-xl border border-primary/[0.06] p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-primary">{teamFollowUps ? "Team follow-ups" : "Follow-ups"}</p>
                <p className="text-[11px] text-primary/45">
                  {teamFollowUps
                    ? "Pending for call centre managers and agents"
                    : "Due or overdue, and every open follow-up"}
                </p>
              </div>
              <Link
                to="/dashboard/follow-ups"
                onClick={() => setOpen(false)}
                className="shrink-0 text-[11px] font-semibold text-accent-dark"
              >
                Open
              </Link>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Count label="Pending" value={followPending} />
              <Count label="Total follow-ups" value={followTotal} />
            </div>
            <ul className="mt-2 space-y-1">
              {followUps.length === 0 ? (
                <li className="text-[11px] text-primary/40">No follow-ups waiting.</li>
              ) : (
                followUps.map((row) => (
                  <li key={row.id}>
                    <Link
                      to={
                        teamFollowUps
                          ? "/dashboard/follow-ups"
                          : `/dashboard/log-contact?caseId=${encodeURIComponent(row.caseId || "")}`
                      }
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-primary/[0.03]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-primary">{row.customer}</span>
                        <span className="block truncate text-[10px] text-primary/45">
                          {teamFollowUps
                            ? `Agent ${row.agent_name || "Unassigned"} · Manager ${row.manager_name || "None assigned"}`
                            : row.caseId}
                          {!teamFollowUps && row.notes ? ` · ${row.notes}` : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-accent-dark">
                        {teamFollowUps ? "View" : "Log"}
                      </span>
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="mt-2 rounded-xl border border-primary/[0.06] p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-primary">Assignments</p>
                <p className="text-[11px] text-primary/45">
                  {isAgent ? "Open cases on your book" : "Cases still to assign"}
                </p>
              </div>
              <Link
                to={assignHref}
                onClick={() => setOpen(false)}
                className="shrink-0 text-[11px] font-semibold text-accent-dark"
              >
                Collection cases
              </Link>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Count label="Today" value={Number(assignments.today || 0)} />
              <Count label="Total open" value={Number(assignments.total || 0)} />
              <Count label="Pending" value={assignPending} />
              <Count label="Completed today" value={Number(assignments.completed || 0)} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
